import { Grid, SegmentedControl, Stack, TextInput } from "@mantine/core";
import { Loading } from "components";
import CreatableSelect from "components/ui/CreatableSelect/CreatableSelect";
import CustomText from "components/ui/CustomText/CustomText";
import { BankAccountResponseType } from "constants/types/bankAccount";
import { SelectDataType } from "constants/types/common";
import { memo } from "react";
import { BankAccountFormValues, PaymentMethod, emptyBankAccount } from "./config";

const spanHalf = { base: 12, md: 6 };

const paymentMethodData: { label: string; value: PaymentMethod }[] = [
  { label: "Tài khoản ngân hàng", value: "bank" },
  { label: "Tiền mặt", value: "cash" },
];

type BankAccountTextField = "bankName" | "accountNumber" | "accountHolder";

/**
 * Component thuần, không đọc Formik context: nhận value/onChange từ ngoài.
 * Nhờ vậy dùng được ở cả form đơn (ReceiptCreateForm) lẫn từng dòng của form
 * bulk (BulkReceiptRow) mà không kéo theo re-render toàn bộ danh sách dòng
 * mỗi khi context Formik đổi (điều sẽ xảy ra nếu dùng useFormikContext trực
 * tiếp bên trong một component được lặp lại nhiều lần).
 */
interface BankAccountFieldsProps {
  paymentMethod: PaymentMethod;
  onPaymentMethodChange: (value: PaymentMethod) => void;
  value: BankAccountFormValues;
  onChange: (value: BankAccountFormValues) => void;
  onFieldBlur?: (field: BankAccountTextField) => void;
  errors?: Partial<Record<BankAccountTextField, string>>;
  touched?: Partial<Record<BankAccountTextField, boolean>>;
  bankAccountList: BankAccountResponseType[];
  bankAccountOptions: SelectDataType[];
  isLoadingBankAccounts?: boolean;
  /** Ẩn nhãn "Phương thức thanh toán" khi ngữ cảnh bên ngoài đã có tiêu đề tương đương. */
  showLabel?: boolean;
}

const BankAccountFields = ({
  paymentMethod,
  onPaymentMethodChange,
  value,
  onChange,
  onFieldBlur,
  errors,
  touched,
  bankAccountList,
  bankAccountOptions,
  isLoadingBankAccounts = false,
  showLabel = true,
}: BankAccountFieldsProps) => {
  const isDisabled = !!value.id;

  const handlePaymentMethodChange = (next: string) => {
    onPaymentMethodChange(next as PaymentMethod);
    if (next === "cash") {
      onChange(emptyBankAccount());
    }
  };

  const handleAccountSelect = (id: string | null) => {
    if (!id) {
      onChange(emptyBankAccount());
      return;
    }
    const account = bankAccountList.find((a) => a.id === id);
    if (account) {
      onChange({
        id,
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        accountHolder: account.accountHolder,
        isActive: account.isActive,
      });
    }
  };

  const handleTextChange = (field: BankAccountTextField, text: string) => {
    onChange({ ...value, [field]: text });
  };

  const fieldError = (field: BankAccountTextField) =>
    touched?.[field] && errors?.[field] ? errors[field] : null;

  return (
    <Stack gap="sm">
      {showLabel && (
        <CustomText variant="product-label" fw={700} c="blue.7">
          Phương thức thanh toán
        </CustomText>
      )}

      <SegmentedControl
        data={paymentMethodData}
        value={paymentMethod}
        onChange={handlePaymentMethodChange}
      />

      {paymentMethod === "bank" && (
        <Stack gap="sm" pos="relative">
          <Loading visible={isLoadingBankAccounts} variant="overlay" />

          <CreatableSelect
            label="Chọn tài khoản ngân hàng có sẵn"
            placeholder="Tìm kiếm tài khoản..."
            allowCreate={false}
            clearable
            data={bankAccountOptions}
            value={value.id || null}
            onChange={handleAccountSelect}
          />

          <Grid>
            <Grid.Col span={spanHalf}>
              <TextInput
                label="Tên ngân hàng"
                placeholder="Techcombank"
                withAsterisk={!isDisabled}
                disabled={isDisabled}
                value={value.bankName}
                onChange={(e) => handleTextChange("bankName", e.target.value)}
                onBlur={() => onFieldBlur?.("bankName")}
                error={fieldError("bankName")}
              />
            </Grid.Col>
            <Grid.Col span={spanHalf}>
              <TextInput
                label="Số tài khoản"
                placeholder="9876543210"
                withAsterisk={!isDisabled}
                disabled={isDisabled}
                value={value.accountNumber}
                onChange={(e) => handleTextChange("accountNumber", e.target.value)}
                onBlur={() => onFieldBlur?.("accountNumber")}
                error={fieldError("accountNumber")}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12 }}>
              <TextInput
                label="Chủ tài khoản"
                placeholder="CONG TY TNHH XYZ"
                withAsterisk={!isDisabled}
                disabled={isDisabled}
                value={value.accountHolder}
                onChange={(e) => handleTextChange("accountHolder", e.target.value)}
                onBlur={() => onFieldBlur?.("accountHolder")}
                error={fieldError("accountHolder")}
              />
            </Grid.Col>
          </Grid>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(BankAccountFields);
